import React, { useState, useCallback, useEffect, useRef } from 'react';
import Text from '../Text/Text';
import './ReleaseConfirmationModal.css';
import LabeledValue from '../LabeledValue';

interface ReleaseConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  transactionDetails: {
    id: string;
    totalAmount?: number;
    sellerUserId?: string;
    itemDescription: string;
  };
}

/**
 * ReleaseConfirmationModal component
 * 
 * This component displays a confirmation modal for releasing funds.
 * It handles various states (initial, loading, error, success) and
 * provides robust error handling for network failures and other issues.
 */
export const ReleaseConfirmationModal: React.FC<ReleaseConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  transactionDetails
}) => {
  // Modal states: 'initial', 'loading', 'error', 'success'
  const [status, setStatus] = useState<'initial' | 'loading' | 'error' | 'success'>('initial');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);
  
  // Ref for tracking if component is mounted
  const isMounted = useRef(true);
  
  // Ref for modal content (for focus management)
  const modalContentRef = useRef<HTMLDivElement>(null);
  
  // Handle escape key press to close modal
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscapeKey);
    
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);
  
  // Focus management
  useEffect(() => {
    if (isOpen && modalContentRef.current) {
      // Store the active element to restore focus later
      const activeElement = document.activeElement;
      
      // Focus the modal content
      modalContentRef.current.focus();
      
      // Restore focus when modal closes
      return () => {
        if (activeElement instanceof HTMLElement) {
          activeElement.focus();
        }
      };
    }
  }, [isOpen]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  
  // Handle confirmation with retry logic and exponential backoff
  const handleConfirm = useCallback(async () => {
    try {
      setStatus('loading');
      
      // If this is a retry, implement exponential backoff
      if (retryCount > 0) {
        const backoffTime = Math.min(1000 * Math.pow(2, retryCount), 8000); // Max 8 seconds
        await new Promise(resolve => setTimeout(resolve, backoffTime));
      }
      
      await onConfirm();
      
      if (isMounted.current) {
        setStatus('success');
        
        // Close modal after showing success message
        setTimeout(() => {
          if (isMounted.current) {
            onClose();
          }
        }, 1500);
      }
    } catch (error) {
      if (isMounted.current) {
        setStatus('error');
        setErrorMessage(
          error instanceof Error 
            ? error.message 
            : 'An error occurred while releasing funds. Please try again.'
        );
      }
    }
  }, [onConfirm, retryCount, onClose]);
  
  // Handle retry
  const handleRetry = useCallback(() => {
    setRetryCount(prev => prev + 1);
    handleConfirm();
  }, [handleConfirm]);
  
  // Implement timeout for long-running requests
  useEffect(() => {
    if (status === 'loading') {
      const timeoutId = setTimeout(() => {
        if (isMounted.current && status === 'loading') {
          setStatus('error');
          setErrorMessage('The request is taking longer than expected. Please wait or try again later.');
        }
      }, 10000); // 10 seconds timeout
      
      return () => clearTimeout(timeoutId);
    }
  }, [status]);
  
  // If modal is not open, don't render anything
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="modal-content"
        ref={modalContentRef}
        onClick={e => e.stopPropagation()}
        tabIndex={0}
        role="dialog"
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        {status === 'initial' && (
          <>
            <h2 id="modal-title" className="release-funds-text-h2">Confirm Fund Release</h2>
            <Text className="text-body">
              You are about to release <strong>${transactionDetails.totalAmount ? transactionDetails.totalAmount.toFixed(2) : '0.00'}</strong>
            </Text>
            <div className="transaction-summary">
              <div>
                <Text className="text-body">{transactionDetails.itemDescription || 'Item'}</Text>
              </div>
              <div>
                <LabeledValue label='Seller Id' value={transactionDetails.sellerUserId} rightAligned={true} />
              </div>
              <div>
                <LabeledValue label='Transaction ID' value={transactionDetails.id} rightAligned={true} />
              </div>  
            </div>
            <Text className="text-alert">
              Once released, funds cannot be recovered. Are you sure you want to proceed?
            </Text>
            <div className="modal-actions">
              <button className="cancel-button" onClick={onClose}>
                Cancel
              </button>
              <button className="confirm-button" onClick={handleConfirm}>
                Release Funds
              </button>
            </div>
          </>
        )}
        
        {status === 'loading' && (
          <>
            <div className="loading-spinner"></div>
            <Text className="text-body text-center">
              Processing your request...
            </Text>
          </>
        )}
        
        {status === 'error' && (
          <>
            <Text className="text-h2 text-error">Error</Text>
            <Text className="text-body">
              {errorMessage || 'An error occurred while releasing funds.'}
            </Text>
            <div className="modal-actions">
              <button className="cancel-button" onClick={onClose}>
                Cancel
              </button>
              <button className="confirm-button" onClick={handleRetry}>
                Retry
              </button>
            </div>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="success-icon">✓</div>
            <Text className="text-h2 text-success">Success!</Text>
            <Text className="text-body text-center">
              Funds have been released successfully.
            </Text>
          </>
        )}
      </div>
    </div>
  );
};